import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowDown, ChevronDown, Menu, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { PAGE_LABELS, SEO_PAGES } from "@/data/seoPages";
import { useEditorialMotion } from "@/hooks/useEditorialMotion";
import "@/pages/CinematicLanding.css";
import "@/pages/CinematicPages.css";

function DamerowBrand({ footer = false }) {
  return <a href="/" className={`dc-brand ${footer ? "dc-brand-footer" : ""}`} aria-label="WebDesign Damerow Startseite">
    <span className="dc-brand-frame"><img className="dc-brand-original" src="/images/armin-damerow-wb-original.png" alt="Originales WB-Logo von Armin Damerow mit Berliner Fernsehturm" width="614" height="614" loading={footer ? "lazy" : "eager"} decoding="async" /></span>
    <span className="dc-wordmark"><span>WebDesign</span><strong>Damerow<span className="dc-brand-dot">.</span></strong></span>
  </a>;
}

export function CinemaNav() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const servicesRef = useRef(null);
  const navRef = useRef(null);
  const { pathname } = useLocation();
  const home = pathname === "/";
  const anchor = (id) => `${home ? "" : "/"}#${id}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 25);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open && !servicesOpen) return undefined;
    const dismiss = (event) => {
      if (event.key === "Escape") {
        if (open) { setOpen(false); menuRef.current?.focus(); }
        else { setServicesOpen(false); servicesRef.current?.focus(); }
      }
    };
    const outside = (event) => { if (!navRef.current?.contains(event.target)) { setOpen(false); setServicesOpen(false); } };
    window.addEventListener("keydown", dismiss);
    document.addEventListener("pointerdown", outside);
    return () => { window.removeEventListener("keydown", dismiss); document.removeEventListener("pointerdown", outside); };
  }, [open, servicesOpen]);

  const close = () => { setOpen(false); setServicesOpen(false); };
  return <header ref={navRef} className={`dc-header ${scrolled ? "dc-is-scrolled" : ""}`} data-testid="cinema-header">
    <DamerowBrand />
    <nav className="dc-desktop-nav" aria-label="Hauptnavigation">
      <a href={anchor("arbeiten")}>Arbeiten</a>
      <div className="dc-nav-disclosure"><button ref={servicesRef} onClick={() => setServicesOpen(!servicesOpen)} aria-expanded={servicesOpen} aria-controls="dc-service-navigation">Leistungen <ChevronDown size={13} /></button>
        {servicesOpen && <div id="dc-service-navigation" className="dc-service-navigation">{Object.entries(SEO_PAGES).map(([key, page]) => <a key={key} href={`/${page.slug}`} aria-current={pathname === `/${page.slug}` ? "page" : undefined} onClick={close}><span>{PAGE_LABELS[key]}</span><ArrowUpRight size={15} /></a>)}<a href={anchor("leistungen")} onClick={close}>Buchung & Automatisierung<ArrowUpRight size={15} /></a></div>}
      </div>
      <a href={anchor("pakete")}>Preise</a>
    </nav>
    <a className="dc-header-contact" href={home ? "#analyse" : "/kontakt"}>Projekt besprechen <ArrowUpRight size={17} /></a>
    <button ref={menuRef} className="dc-menu-toggle" aria-expanded={open} aria-controls="dc-mobile-menu" aria-label={open ? "Menü schließen" : "Menü öffnen"} onClick={() => { setOpen(!open); setServicesOpen(false); }}>{open ? <X /> : <Menu />}</button>
    {open && <nav id="dc-mobile-menu" className="dc-mobile-menu" aria-label="Mobile Hauptnavigation">
      <a href={anchor("arbeiten")} onClick={close}>Arbeiten<ArrowUpRight /></a>
      {Object.entries(SEO_PAGES).map(([key, page]) => <a href={`/${page.slug}`} key={key} aria-current={pathname === `/${page.slug}` ? "page" : undefined} onClick={close}>{PAGE_LABELS[key]}<ArrowUpRight /></a>)}
      <a href={anchor("leistungen")} onClick={close}>Buchung & Abläufe<ArrowUpRight /></a>
      <a href={anchor("pakete")} onClick={close}>Preise<ArrowUpRight /></a>
      <a href="/kontakt" onClick={close}>Kontakt<ArrowUpRight /></a>
    </nav>}
  </header>;
}

function Imprint() {
  return <Dialog><DialogTrigger asChild><button data-testid="footer-impressum">Impressum <ArrowUpRight size={12} /></button></DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Impressum</DialogTitle><DialogDescription className="sr-only">Rechtliche Informationen zu Impressum.</DialogDescription></DialogHeader>
      <div className="text-sm text-slate-600 leading-relaxed space-y-3"><p>Angaben gemäß § 5 DDG</p><p>Armin Damerow<br />WebDesign Damerow<br />Rosenthaler Straße 15<br />13127 Berlin</p><p>E-Mail: kontakt@webdesign-damerow.de</p></div>
    </DialogContent></Dialog>;
}

function MotionControl() {
  const [reduced, setReduced] = useState(() => document.documentElement.dataset.reduceMotion === "true");
  return <button className="dc-motion-control" aria-pressed={reduced} onClick={() => {
    const next = !reduced;
    setReduced(next);
    document.documentElement.dataset.reduceMotion = String(next);
    window.dispatchEvent(new Event("damerow-motion-change"));
  }}>Bewegung reduzieren{reduced ? ": an" : ": aus"}</button>;
}

export function CinemaFooter() {
  return <footer className="dc-site-footer" data-testid="marketing-footer">
    <div className="dc-footer-top"><DamerowBrand footer /><span>GESTALTUNG MIT CHARAKTER.<br />PERSÖNLICH AUS BERLIN.</span><a href="/kontakt" className="dc-footer-project">Ihr nächstes Projekt.<ArrowUpRight /></a></div>
    <div className="dc-footer-links"><div><span>VERBINDEN WIR UNS.</span><a href="mailto:kontakt@webdesign-damerow.de" data-testid="footer-email-link">kontakt@webdesign-damerow.de<ArrowUpRight size={16} /></a><p>Websites, Online-Buchung und digitale Abläufe für kleine Unternehmen. Aus Berlin, deutschlandweit.</p></div><nav aria-label="Weiterführende Seiten">{Object.entries(SEO_PAGES).map(([key, page]) => <a key={key} href={`/${page.slug}`} data-testid={`footer-service-link-${key}`}>{PAGE_LABELS[key]}<ArrowUpRight size={12} /></a>)}<a href="/kontakt" data-testid="footer-contact-link">Kontakt<ArrowUpRight size={12} /></a><a href="/#pakete" data-testid="footer-pricing-link">Angebote & Preise<ArrowUpRight size={12} /></a><a href="/demo/friseur">Salon-Konzept<ArrowUpRight size={12} /></a><a href="/datenschutz" data-testid="footer-privacy-link">Datenschutz<ArrowUpRight size={12} /></a><Imprint /></nav></div>
    <MotionControl /><div className="dc-footer-bottom"><p>© 2026 WebDesign Damerow</p><p>Kein Ausweis von Umsatzsteuer aufgrund Anwendung der Kleinunternehmerregelung gemäß § 19 UStG.</p><a href="#page-top" aria-label="Zum Seitenanfang">Nach oben<ArrowDown size={12} /></a></div>
  </footer>;
}

export function CinemaFrame({ children }) {
  const rootRef = useRef(null);
  const { pathname } = useLocation();
  useEditorialMotion(rootRef, pathname);
  return <div className="dam-cinema dc-inner-page" ref={rootRef} id="page-top"><a className="dc-skip" href="#page-content">Zum Inhalt springen</a><CinemaNav />{children}<CinemaFooter />{pathname !== "/datenschutz" && <a className="dc-mobile-cta" href={pathname === "/kontakt" ? "#projektanfrage" : "/kontakt"}>Ihr Projekt besprechen<ArrowUpRight size={18} /></a>}</div>;
}
